import type { ScreenerOIDataPoint } from '@/types'

export const SCREENER_CHART_GROUP = 'oi-screener-linked-charts'

export const EXCHANGE_COLORS = [
  '#2dd4bf',
  '#60a5fa',
  '#f59e0b',
  '#f472b6',
  '#a78bfa',
  '#34d399',
  '#fb7185',
  '#22d3ee',
  '#facc15',
  '#818cf8',
]

export function exchangeColor(index: number): string {
  return EXCHANGE_COLORS[index % EXCHANGE_COLORS.length]
}

export function isFreshOI(point: Pick<ScreenerOIDataPoint, 'quality'>): boolean {
  // An exchange may return current OI without exposing its update time; that valid
  // observed_snapshot is not a failed refresh or a carried value.
  switch (String(point.quality || '').trim().toLowerCase()) {
    case '':
    case 'fresh':
    case 'aggregate':
    case 'observed':
    case 'observed_snapshot':
      return true
    default:
      return false
  }
}

export function isPlottableOI(
  point: Pick<ScreenerOIDataPoint, 'quality' | 'sampleKind'>,
): boolean {
  if (isFreshOI(point)) return true
  // Continuous carried_forward is a successful API observation of an older
  // source value. Plot the actual receipt while preserving its stale badge.
  // Legacy carried points can be synthetic and must remain gaps.
  return point.sampleKind === 'continuous'
    && String(point.quality || '').trim().toLowerCase() === 'carried_forward'
}

export function oiQualityDescription(
  point: Pick<ScreenerOIDataPoint, 'quality' | 'sampleKind'>,
): string | null {
  const quality = String(point.quality || '').trim().toLowerCase()
  if (quality === 'observed' || quality === 'observed_snapshot') {
    return 'Observation received; exchange update time unavailable'
  }
  if (isFreshOI(point)) return null
  if (point.sampleKind === 'continuous') {
    return 'Response received with an older exchange timestamp'
  }
  return 'Carried value; no new observation'
}

export function oiSeriesWithGaps(
  points: ScreenerOIDataPoint[],
  interval: string,
): Array<[number, number | null]> {
  const stepSeconds = ({ '5s': 5, '1m': 60, '5m': 300, '30m': 1800, '1h': 3600, '4h': 14400, '1d': 86400 }[interval] || 60)
  const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp)
  const series: Array<[number, number | null]> = []
  let previousTimestamp: number | null = null
  for (const point of sorted) {
    if (
      previousTimestamp != null
      && point.timestamp - previousTimestamp > stepSeconds * 1.5
    ) {
      series.push([(previousTimestamp + stepSeconds) * 1000, null])
    }
    series.push([
      point.timestamp * 1000,
      isPlottableOI(point) ? point.value : null,
    ])
    previousTimestamp = point.timestamp
  }
  return series
}

export function formatCompact(value: number, digits = 2): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(digits)}B`
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(digits)}M`
  if (abs >= 1_000) return `${(value / 1_000).toFixed(digits)}K`
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

export function formatUsd(value: number, digits = 2): string {
  return `$${formatCompact(value, digits)}`
}

export function formatUtc(timestampMs: number, withSeconds = true): string {
  if (!Number.isFinite(timestampMs)) return 'N/A'
  const value = new Date(timestampMs)
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }
  if (withSeconds) options.second = '2-digit'
  return `${new Intl.DateTimeFormat('en-GB', options).format(value)} UTC`
}

export function formatUtcAxis(timestampMs: number, interval: string): string {
  const date = new Date(timestampMs)
  if (!Number.isFinite(date.getTime())) return ''
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    second: interval === '5s' ? '2-digit' : undefined,
    hour12: false,
  }).format(date)
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
  }).format(date)
  return `${day}\n${time}`
}

export function findLastKnownIndex(timestampsMs: number[], targetMs: number): number {
  if (timestampsMs.length === 0 || targetMs < timestampsMs[0]) return -1
  let left = 0
  let right = timestampsMs.length - 1
  while (left <= right) {
    const mid = (left + right) >> 1
    if (timestampsMs[mid] <= targetMs) left = mid + 1
    else right = mid - 1
  }
  return right
}

export function lastKnownPoint<T extends ScreenerOIDataPoint>(
  points: T[],
  targetSeconds?: number | null,
): { point: T | null; ageSeconds: number | null } {
  if (points.length === 0) return { point: null, ageSeconds: null }
  const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp)
  if (targetSeconds == null) return { point: sorted[sorted.length - 1], ageSeconds: null }
  const timestamps = sorted.map((point) => point.timestamp * 1000)
  const targetMs = Number(targetSeconds) * 1000
  const index = findLastKnownIndex(timestamps, targetMs)
  if (index < 0) return { point: null, ageSeconds: null }
  const point = sorted[index]
  return {
    point,
    ageSeconds: Math.max(0, Math.floor(Number(targetSeconds) - point.timestamp)),
  }
}

export function formatAge(ageSeconds: number | null): string {
  if (ageSeconds == null || ageSeconds <= 0) return ''
  if (ageSeconds < 60) return `${ageSeconds}s old`
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m old`
  if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)}h old`
  return `${Math.floor(ageSeconds / 86400)}d old`
}
